import React, { useCallback, useEffect, useState } from "react";
import { Container } from "reactstrap";
import BreadCrumb from "components/common/BreadCrumb";
import { Link, useParams } from "react-router-dom";
import PageLoader from "components/common/PageLoader";
import { toast } from "react-toastify";
import type { Amc } from "features/Amc/types/amc.types";
import { getAmc } from "features/Amc/services/AmcService";
import { getDocumentTitle } from "utils/helpers";
import AmcContractOverviewContext from "./AmcContractOverviewContext";
import AmcContractOverviewSection from "./AmcContractOverviewSection";

function ViewAmcDetails() {
  document.title = getDocumentTitle("AMC overview");
  const { id } = useParams();
  const [record, setRecord] = useState<Amc | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    const numericId = Number(id);
    if (!Number.isFinite(numericId)) {
      setRecord(null);
      setLoadError(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(false);
    try {
      const response = await getAmc(numericId);
      if (response.success && response.data) {
        console.log(response.data);
        setRecord(response.data);
      } else {
        setRecord(null);
        setLoadError(true);
      }
    } catch (err) {
      console.warn(err);
      setRecord(null);
      setLoadError(true);
      toast.error("Could not load contract");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <PageLoader message="Loading contract…" />;
  }

  if (loadError || !record) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Contract overview"
            pageTitle="AMC Management"
            items={[{ label: "Contracts", path: "/amc-management" }]}
          />
          <p className="text-muted">This contract could not be loaded.</p>
          <Link to="/amc-management" className="btn btn-light btn-sm">
            Back to contracts
          </Link>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Contract overview"
          pageTitle="AMC Management"
          items={[{ label: "Contracts", path: "/amc-management" }]}
        />

        <AmcContractOverviewContext.Provider
          value={{ contract: record, refetch: load }}
        >
          <AmcContractOverviewSection />
        </AmcContractOverviewContext.Provider>
      </Container>
    </div>
  );
}

export default ViewAmcDetails;
